/**
 * Advanced Question Bank: Domain 1 — Abstract Reasoning & Patterns (الاستدلال المجرد والأنماط)
 * 15 Psychometrically Rigorous Generative Template Families (Fluid Intelligence Gf / Raven APM)
 */

import { render3x3Matrix, renderHorizontalSeries, renderOptionSvg, DrawShapes, wrapSvg } from './svg-builder.js';

export const ADVANCED_ABSTRACT_TEMPLATES = [
  // 1. Dual-Shape Boolean Logic (XOR Rule across columns)
  {
    id: 'abs_xor_boolean_matrix',
    domain: 'abstract_reasoning',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 45,
    generate(prng, difficulty = 2) {
      // 4 possible binary components (top-left, top-right, bottom-left, bottom-right quadrants)
      const shapes = ['circle', 'square', 'triangle', 'diamond'];
      const chosen = prng.sample(shapes, 3);
      const [s1, s2, s3] = chosen;

      // Row rule: Cell 3 is XOR of Cell 1 and Cell 2 (present in 1 or 2, but not both)
      // We generate rows such that Row 3 also obeys XOR
      const patterns = [
        [[1, 0], [0, 1], [1, 1]],
        [[1, 1], [1, 0], [0, 1]],
        [[0, 1], [1, 1], [1, 0]],
        [[1, 0], [1, 1], [0, 1]],
        [[1, 1], [0, 1], [1, 0]]
      ];
      const p1 = prng.pick(patterns);
      const p2 = prng.pick(patterns);
      // Row 3: XOR of row 1 and row 2 in each column
      const r3c1 = [p1[0][0] ^ p2[0][0], p1[0][1] ^ p2[0][1]];
      const r3c2 = [p1[1][0] ^ p2[1][0], p1[1][1] ^ p2[1][1]];
      const r3c3 = [r3c1[0] ^ r3c2[0], r3c1[1] ^ r3c2[1]];

      const drawBits = (bits) => (s) => {
        const d = s * 0.22;
        let out = '';
        if (bits[0]) out += `<circle cx="${-d}" cy="0" r="${s * 0.14}" fill="#38bdf8" stroke="currentColor" stroke-width="1.5"/>`;
        if (bits[1]) out += `<rect x="${d - s * 0.14}" y="${-s * 0.14}" width="${s * 0.28}" height="${s * 0.28}" rx="3" fill="#ec4899" stroke="currentColor" stroke-width="1.5"/>`;
        if (!bits[0] && !bits[1]) out += `<circle cx="0" cy="0" r="3" fill="currentColor" opacity="0.25"/>`;
        return out;
      };

      const cells = [
        drawBits(p1[0]), drawBits(p1[1]), drawBits(p1[2]),
        drawBits(p2[0]), drawBits(p2[1]), drawBits(p2[2]),
        drawBits(r3c1),  drawBits(r3c2),  drawBits(r3c3)
      ];

      const correctBits = r3c3;
      const allPossibleBits = [[0, 0], [1, 0], [0, 1], [1, 1]];
      const distractors = allPossibleBits.filter(b => !(b[0] === correctBits[0] && b[1] === correctBits[1]));
      const allChoices = prng.shuffle([correctBits, ...distractors]);
      const correctIndex = allChoices.findIndex(b => b[0] === correctBits[0] && b[1] === correctBits[1]);

      const options = allChoices.map((b, idx) => ({
        id: String.fromCharCode(65 + idx),
        svg: renderOptionSvg(drawBits(b), 100, `XOR Option ${idx + 1}`),
        value: b.join(',')
      }));

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: 'تتبع عناصر كل صف وعمود قانون البوابات المنطقية (XOR: العنصر يظهر في الخلية الثالثة إذا وُجد في إحدى الخليتين فقط وليس كلتيهما). ما هو الشكل الناقص في الخلية الأخيرة؟',
        questionText_en: 'The matrix follows a strict Boolean XOR rule across rows and columns (an element appears in the third cell if and only if it is present in exactly one of the previous two cells). Which option completes the matrix?',
        stimulusHtml: render3x3Matrix(cells, 310),
        options,
        correctIndex,
        explanation_ar: `بتطبيق البوابة المنطقية XOR أفقيًا ورأسيًا: الدائرة الزرقاء توجد في الخلية الأولى ولا توجد في الثانية، والمربع الوردي يتبع نفس المنطق الحصري، مما ينتج التوليفة المنطقية المحددة في الخيار (${String.fromCharCode(65 + correctIndex)}).`,
        explanation_en: `Applying the XOR logic across the third row: elements present in only one of the preceding cells carry forward to the target cell, establishing (${String.fromCharCode(65 + correctIndex)}) as the strictly correct configuration.`
      };
    }
  },

  // 2. Nested Layer Subtraction (Outer Layer - Inner Layer)
  {
    id: 'abs_nested_layer_subtraction',
    domain: 'abstract_reasoning',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 40,
    generate(prng, difficulty = 2) {
      const step = prng.pick([1, 2]);
      const baseOuter = prng.int(4, 6);
      const baseInner = prng.int(1, 2);

      const drawConcentricRingDots = (outer, inner) => (s) => {
        let out = '';
        out += `<circle cx="0" cy="0" r="${s * 0.42}" fill="none" stroke="currentColor" stroke-width="1.8" stroke-dasharray="3,3"/>`;
        out += `<circle cx="0" cy="0" r="${s * 0.22}" fill="none" stroke="currentColor" stroke-width="1.8"/>`;
        // Outer dots
        for (let i = 0; i < outer; i++) {
          const a = (i * 2 * Math.PI) / (outer || 1);
          out += `<circle cx="${(Math.cos(a) * s * 0.42).toFixed(1)}" cy="${(Math.sin(a) * s * 0.42).toFixed(1)}" r="3.4" fill="#38bdf8"/>`;
        }
        // Inner dots
        for (let j = 0; j < inner; j++) {
          const a = (j * 2 * Math.PI) / (inner || 1);
          out += `<circle cx="${(Math.cos(a) * s * 0.22).toFixed(1)}" cy="${(Math.sin(a) * s * 0.22).toFixed(1)}" r="3.2" fill="#ec4899"/>`;
        }
        return out;
      };

      const cells = [];
      for (let r = 0; r < 3; r++) {
        for (let c = 0; c < 3; c++) {
          const outer = baseOuter + r;
          const inner = baseInner + c * step;
          cells.push(drawConcentricRingDots(outer, inner));
        }
      }

      const targetOuter = baseOuter + 2;
      const targetInner = baseInner + 2 * step;

      const distractors = [
        { o: targetOuter, i: targetInner - 1 },
        { o: targetOuter - 1, i: targetInner },
        { o: targetOuter + 1, i: targetInner + 1 }
      ];

      const all = prng.shuffle([{ o: targetOuter, i: targetInner }, ...distractors]);
      const correctIndex = all.findIndex(item => item.o === targetOuter && item.i === targetInner);

      const options = all.map((it, idx) => ({
        id: String.fromCharCode(65 + idx),
        svg: renderOptionSvg(drawConcentricRingDots(it.o, it.i), 100, `Option ${idx + 1}`),
        value: `${it.o}-${it.i}`
      }));

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: 'تزيد النقاط على المحيط الخارجي بمقدار 1 مع كل صف لأسفل، بينما تزيد النقاط في الدائرة الداخلية بمقدار ثابت عبر الأعمدة لليمين. ما هو التشكيل الصحيح للخلية المفقودة؟',
        questionText_en: 'Outer ring dots increment by 1 per descending row, while inner ring dots increase systematically across columns to the right. Which configuration completes the missing cell?',
        stimulusHtml: render3x3Matrix(cells, 310),
        options,
        correctIndex,
        explanation_ar: `في الصف الثالث تكون النقاط الخارجية = ${targetOuter}، وفي العمود الثالث تكون النقاط الداخلية = ${targetInner}. الخيار الوحيد المطابق هو (${String.fromCharCode(65 + correctIndex)}).`,
        explanation_en: `Row 3 mandates ${targetOuter} outer dots, while Column 3 requires ${targetInner} inner dots, definitively isolating option (${String.fromCharCode(65 + correctIndex)}).`
      };
    }
  },

  // 3. Polygon Vertex Token Traversal (Harmonic Motion)
  {
    id: 'abs_grid_vertex_movement',
    domain: 'abstract_reasoning',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 40,
    generate(prng, difficulty = 2) {
      const vertices = 6; // Regular hexagon
      const step = difficulty === 3 ? 2 : 1;
      const startV = prng.int(0, vertices - 1);

      const drawHexToken = (activeVertex) => (s) => {
        const r = s * 0.42;
        let pts = [];
        for (let i = 0; i < vertices; i++) {
          const a = (i * 2 * Math.PI) / vertices - Math.PI / 2;
          pts.push(`${(Math.cos(a) * r).toFixed(1)},${(Math.sin(a) * r).toFixed(1)}`);
        }
        let out = `<polygon points="${pts.join(' ')}" fill="none" stroke="currentColor" stroke-width="1.8" opacity="0.6"/>`;
        // Draw active token
        const aActive = (activeVertex * 2 * Math.PI) / vertices - Math.PI / 2;
        const tx = (Math.cos(aActive) * r).toFixed(1);
        const ty = (Math.sin(aActive) * r).toFixed(1);
        out += `<circle cx="${tx}" cy="${ty}" r="${s * 0.11}" fill="#38bdf8" stroke="#ffffff" stroke-width="2"/>`;
        return out;
      };

      const series = [];
      for (let i = 0; i < 5; i++) {
        const v = (startV + i * step) % vertices;
        series.push(i === 4 ? null : drawHexToken(v));
      }

      const correctV = (startV + 4 * step) % vertices;
      const distractorV = [0, 1, 2, 3, 4, 5].filter(v => v !== correctV);
      const sampledD = prng.sample(distractorV, 3);
      const allV = prng.shuffle([correctV, ...sampledD]);
      const correctIndex = allV.indexOf(correctV);

      const options = allV.map((v, idx) => ({
        id: String.fromCharCode(65 + idx),
        svg: renderOptionSvg(drawHexToken(v), 90, `Vertex ${v}`),
        value: v
      }));

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: 'تتحرك النقطة الزرقاء على رؤوس المسدس المنتظم باتجاه عقارب الساعة بمعدل إزاحة منتظم. ما هو الموضع الذي يجب أن تحله النقطة عند إشارة الاستفهام؟',
        questionText_en: 'The blue token advances clockwise along the vertices of a regular hexagon at a constant step. Which position correctly completes the sequence?',
        stimulusHtml: renderHorizontalSeries(series, 360),
        options,
        correctIndex,
        explanation_ar: `تتحرك النقطة بمقدار ${step} خطوة باتجاه عقارب الساعة في كل إطار، وبالتالي تصل في الإطار الخامس إلى الرأس المقابل للخيار (${String.fromCharCode(65 + correctIndex)}).`,
        explanation_en: `Advancing ${step} vertices clockwise per frame places the token at vertex index ${correctV} on the fifth step, precisely matching (${String.fromCharCode(65 + correctIndex)}).`
      };
    }
  },

  // 4. Matrix Color Polarity Inversion (Checkerboard Parity)
  {
    id: 'abs_matrix_color_inversion',
    domain: 'abstract_reasoning',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 35,
    generate(prng, difficulty = 2) {
      const baseShape = prng.pick(['circle', 'diamond', 'triangle']);
      const invertRule = (r, c) => (r + c) % 2 === 1;

      const drawPolarityCell = (isInverted) => (s) => {
        const bgFill = isInverted ? '#38bdf8' : 'rgba(255, 255, 255, 0.05)';
        const fgFill = isInverted ? 'rgba(15, 23, 42, 0.95)' : '#38bdf8';
        let out = `<rect x="${-s * 0.44}" y="${-s * 0.44}" width="${s * 0.88}" height="${s * 0.88}" rx="8" fill="${bgFill}" stroke="currentColor" stroke-width="1.6"/>`;
        if (baseShape === 'circle') {
          out += `<circle cx="0" cy="0" r="${s * 0.24}" fill="${fgFill}"/>`;
        } else if (baseShape === 'diamond') {
          const d = s * 0.26;
          out += `<polygon points="0,${-d} ${d},0 0,${d} ${-d},0" fill="${fgFill}"/>`;
        } else {
          const h = s * 0.28;
          out += `<polygon points="0,${-h} ${h},${h * 0.7} ${-h},${h * 0.7}" fill="${fgFill}"/>`;
        }
        return out;
      };

      const cells = [];
      for (let r = 0; r < 3; r++) {
        for (let c = 0; c < 3; c++) {
          cells.push(drawPolarityCell(invertRule(r, c)));
        }
      }

      // (2, 2) is (2+2)%2 = 0 -> not inverted
      const correctInverted = invertRule(2, 2);

      const options = [
        { id: 'A', svg: renderOptionSvg(drawPolarityCell(false), 95, 'Polarity Direct'), value: 'normal' },
        { id: 'B', svg: renderOptionSvg(drawPolarityCell(true), 95, 'Polarity Inverted'), value: 'inverted' },
        { id: 'C', svg: renderOptionSvg((s) => `<circle cx="0" cy="0" r="${s * 0.35}" fill="none" stroke="#ef4444" stroke-width="2"/>`, 95, 'Empty'), value: 'empty' },
        { id: 'D', svg: renderOptionSvg((s) => DrawShapes.cross(s * 0.7, '#f59e0b', 3), 95, 'Cross'), value: 'cross' }
      ];
      const correctIndex = correctInverted ? 1 : 0;

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: 'تتبادل خلايا المصفوفة نمط التباين اللوني (عكس قطبية الخلفية مع الشكل الداخلي) وفق قاعدة شطرنجية تعتمد على زوجية موضع الخلية. ما هو الشكل الصحيح للمربع الأخير؟',
        questionText_en: 'Cells alternate contrast polarity (inverting background and foreground) following a strict parity rule across rows and columns. Which option correctly fills the final cell?',
        stimulusHtml: render3x3Matrix(cells, 310),
        options,
        correctIndex,
        explanation_ar: `موضع الخلية هو الصف الثالث والعمود الثالث (مجموع الإحداثيات زوجي)، مما يقتضي عودة القطبية للوضع الأصلي غير المعكوس، وهو ما يطابق الخيار (${options[correctIndex].id}).`,
        explanation_en: `Coordinates (row 3, col 3) have an even index sum, mandating the non-inverted baseline state represented strictly by (${options[correctIndex].id}).`
      };
    }
  },

  // 5. Binary Tree Branching Depth Progression
  {
    id: 'abs_fractal_branching',
    domain: 'abstract_reasoning',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 40,
    generate(prng, difficulty = 2) {
      const drawTree = (depth) => (s) => {
        let out = '';
        const stemLen = s * 0.35;
        // Base trunk
        out += `<line x1="0" y1="${s * 0.4}" x2="0" y2="${s * 0.1}" stroke="#38bdf8" stroke-width="2.6" stroke-linecap="round"/>`;

        if (depth >= 1) {
          out += `<line x1="0" y1="${s * 0.1}" x2="${-s * 0.25}" y2="${-s * 0.1}" stroke="#38bdf8" stroke-width="2.2" stroke-linecap="round"/>`;
          out += `<line x1="0" y1="${s * 0.1}" x2="${s * 0.25}" y2="${-s * 0.1}" stroke="#38bdf8" stroke-width="2.2" stroke-linecap="round"/>`;
        }
        if (depth >= 2) {
          out += `<line x1="${-s * 0.25}" y1="${-s * 0.1}" x2="${-s * 0.38}" y2="${-s * 0.32}" stroke="#ec4899" stroke-width="1.8" stroke-linecap="round"/>`;
          out += `<line x1="${-s * 0.25}" y1="${-s * 0.1}" x2="${-s * 0.14}" y2="${-s * 0.32}" stroke="#ec4899" stroke-width="1.8" stroke-linecap="round"/>`;
          out += `<line x1="${s * 0.25}" y1="${-s * 0.1}" x2="${s * 0.14}" y2="${-s * 0.32}" stroke="#ec4899" stroke-width="1.8" stroke-linecap="round"/>`;
          out += `<line x1="${s * 0.25}" y1="${-s * 0.1}" x2="${s * 0.38}" y2="${-s * 0.32}" stroke="#ec4899" stroke-width="1.8" stroke-linecap="round"/>`;
        }
        if (depth >= 3) {
          // Leaf nodes
          const leafX = [-s * 0.42, -s * 0.34, -s * 0.18, -s * 0.10, s * 0.10, s * 0.18, s * 0.34, s * 0.42];
          leafX.forEach(lx => {
            out += `<circle cx="${lx}" cy="${-s * 0.38}" r="2.8" fill="#10b981"/>`;
          });
        }
        return out;
      };

      const series = [drawTree(0), drawTree(1), drawTree(2), drawTree(3), null];
      const correctDepth = 3; // On step 5 it has full leaves or depth 3
      // Distractors with broken branches or wrong depth
      const options = [
        { id: 'A', svg: renderOptionSvg(drawTree(3), 95, 'Depth 3 with leaves'), value: 3 },
        { id: 'B', svg: renderOptionSvg(drawTree(2), 95, 'Depth 2 only'), value: 2 },
        { id: 'C', svg: renderOptionSvg(drawTree(1), 95, 'Depth 1 only'), value: 1 },
        { id: 'D', svg: renderOptionSvg((s) => DrawShapes.cross(s * 0.6, '#ec4899', 2), 95, 'Distractor'), value: -1 }
      ];
      const shuffled = prng.shuffle(options);
      const correctIndex = shuffled.findIndex(o => o.value === 3);

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: 'يتضاعف عدد تفرعات الشجرة الهندسي بصورة ثنائية متتالية مع كل خطوة (1 ثم 2 ثم 4 ثم 8 عقد ورقية). ما هو التفرع الصحيح للخطوة الخامسة؟',
        questionText_en: 'The fractal branching bifurcates symmetrically at each generation (1 trunk, 2 limbs, 4 branches, 8 terminal leaves). Which tree matches the fifth state?',
        stimulusHtml: renderHorizontalSeries(series, 360),
        options: shuffled,
        correctIndex,
        explanation_ar: `تتابع المتتالية الهندسية نمط التفرع الثنائي الأسي (2 أس n)، مما يعني أن الخطوة المستهدفة تكتمل بظهور العقد الورقية الثمانية في الخيار (${shuffled[correctIndex].id}).`,
        explanation_en: `Binary bifurcation mandates the terminal generation of 8 leaf nodes at depth 3, accurately represented by option (${shuffled[correctIndex].id}).`
      };
    }
  },

  // 6. Dual-Axis Asymmetric Arm Rotation
  {
    id: 'abs_dual_axis_rotation',
    domain: 'abstract_reasoning',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 45,
    generate(prng, difficulty = 2) {
      const stepA = 45;
      const stepB = -90;
      const baseA = prng.int(0, 3) * 45;
      const baseB = prng.int(0, 3) * 90;

      const drawArms = (a1, a2) => (s) => {
        const rad1 = (a1 * Math.PI) / 180;
        const rad2 = (a2 * Math.PI) / 180;
        const l1 = s * 0.42;
        const l2 = s * 0.28;
        const x1 = Math.cos(rad1) * l1;
        const y1 = Math.sin(rad1) * l1;
        const x2 = Math.cos(rad2) * l2;
        const y2 = Math.sin(rad2) * l2;

        let out = `<circle cx="0" cy="0" r="4.5" fill="#ffffff" stroke="currentColor" stroke-width="1.5"/>`;
        out += `<line x1="0" y1="0" x2="${x1.toFixed(1)}" y2="${y1.toFixed(1)}" stroke="#38bdf8" stroke-width="3.2" stroke-linecap="round"/>`;
        out += `<line x1="0" y1="0" x2="${x2.toFixed(1)}" y2="${y2.toFixed(1)}" stroke="#ec4899" stroke-width="2.4" stroke-linecap="round"/>`;
        out += `<circle cx="${x1.toFixed(1)}" cy="${y1.toFixed(1)}" r="3.5" fill="#38bdf8"/>`;
        out += `<circle cx="${x2.toFixed(1)}" cy="${y2.toFixed(1)}" r="3" fill="#ec4899"/>`;
        return out;
      };

      const series = [];
      for (let i = 0; i < 5; i++) {
        const a1 = (baseA + i * stepA) % 360;
        const a2 = (baseB + i * stepB) % 360;
        series.push(i === 4 ? null : drawArms(a1, a2));
      }

      const targetA = (baseA + 4 * stepA) % 360;
      const targetB = (baseB + 4 * stepB) % 360;

      const distractors = [
        { a: targetA, b: (targetB + 90) % 360 },
        { a: (targetA + 45) % 360, b: targetB },
        { a: (targetA + 90) % 360, b: (targetB - 90) % 360 }
      ];

      const all = prng.shuffle([{ a: targetA, b: targetB }, ...distractors]);
      const correctIndex = all.findIndex(it => it.a === targetA && it.b === targetB);

      const options = all.map((it, idx) => ({
        id: String.fromCharCode(65 + idx),
        svg: renderOptionSvg(drawArms(it.a, it.b), 95, `Option ${idx + 1}`),
        value: `${it.a}-${it.b}`
      }));

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: 'يدور الذراع الأزرق الطويل باتجاه عقارب الساعة بمقدار 45° في كل إطار، بينما يدور الذراع الوردي القصير عكس عقارب الساعة بمقدار 90°. ما هو الشكل المكمل للمتتالية؟',
        questionText_en: 'The long blue arm rotates clockwise by 45° each step, while the short pink arm rotates counter-clockwise by 90°. Which configuration appears at the question mark?',
        stimulusHtml: renderHorizontalSeries(series, 360),
        options,
        correctIndex,
        explanation_ar: `الذراع الأزرق بعد 4 خطوات يكون عند الزاوية ${targetA}°، والذراع الوردي عند ${targetB}°، مما يحدد الخيار (${String.fromCharCode(65 + correctIndex)}) بدقة متناهية.`,
        explanation_en: `Compounding 4 increments of +45° yields ${targetA}° for the primary arm, and 4 increments of -90° yields ${targetB}° for the secondary arm, matching (${String.fromCharCode(65 + correctIndex)}).`
      };
    }
  },

  // 7. Quadrant Cyclic Permutation Matrix
  {
    id: 'abs_quadrant_shuffling',
    domain: 'abstract_reasoning',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 45,
    generate(prng, difficulty = 2) {
      const symbols = ['circle', 'triangle', 'square', 'cross'];
      const [s0, s1, s2, s3] = prng.shuffle(symbols);

      const drawQuad = (order) => (s) => {
        const d = s * 0.22;
        const coords = [
          [-d, -d], // Q1: Top-Left
          [d, -d],  // Q2: Top-Right
          [d, d],   // Q3: Bottom-Right
          [-d, d]   // Q4: Bottom-Left
        ];
        let out = `<rect x="${-s * 0.44}" y="${-s * 0.44}" width="${s * 0.88}" height="${s * 0.88}" rx="8" fill="none" stroke="currentColor" stroke-width="1.5" stroke-dasharray="2,2"/>`;
        out += `<line x1="0" y1="${-s * 0.44}" x2="0" y2="${s * 0.44}" stroke="currentColor" stroke-opacity="0.3" stroke-width="1"/>`;
        out += `<line x1="${-s * 0.44}" y1="0" x2="${s * 0.44}" y2="0" stroke="currentColor" stroke-opacity="0.3" stroke-width="1"/>`;

        for (let i = 0; i < 4; i++) {
          const sym = order[i];
          const [cx, cy] = coords[i];
          if (sym === 'circle') out += `<circle cx="${cx}" cy="${cy}" r="${s * 0.1}" fill="#38bdf8"/>`;
          if (sym === 'triangle') out += `<polygon points="${cx},${cy - s * 0.1} ${cx + s * 0.1},${cy + s * 0.08} ${cx - s * 0.1},${cy + s * 0.08}" fill="#ec4899"/>`;
          if (sym === 'square') out += `<rect x="${cx - s * 0.09}" y="${cy - s * 0.09}" width="${s * 0.18}" height="${s * 0.18}" rx="2" fill="#10b981"/>`;
          if (sym === 'cross') {
            const h = s * 0.09;
            out += `<line x1="${cx - h}" y1="${cy}" x2="${cx + h}" y2="${cy}" stroke="#f59e0b" stroke-width="2.5"/><line x1="${cx}" y1="${cy - h}" x2="${cx}" y2="${cy + h}" stroke="#f59e0b" stroke-width="2.5"/>`;
          }
        }
        return out;
      };

      // Cyclic shift helper
      const rotate = (arr, shift) => arr.map((_, i) => arr[(i + shift) % arr.length]);

      const baseList = [s0, s1, s2, s3];
      const cells = [];
      for (let r = 0; r < 3; r++) {
        for (let c = 0; c < 3; c++) {
          const shift = (r + c) % 4;
          cells.push(drawQuad(rotate(baseList, shift)));
        }
      }

      const targetShift = (2 + 2) % 4; // 0
      const correctOrder = rotate(baseList, targetShift);

      const distractors = [
        rotate(baseList, 1),
        rotate(baseList, 2),
        rotate(baseList, 3)
      ];

      const all = prng.shuffle([correctOrder, ...distractors]);
      const correctIndex = all.findIndex(o => o.join(',') === correctOrder.join(','));

      const options = all.map((ord, idx) => ({
        id: String.fromCharCode(65 + idx),
        svg: renderOptionSvg(drawQuad(ord), 95, `Option ${idx + 1}`),
        value: ord.join(',')
      }));

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: 'تتحرك الرموز الأربعة عبر الأرباع في اتجاه دائري منتظم مع كل انتقال أفقي وعمودي داخل المصفوفة. ما هو الترتيب الصحيح للخلية الأخيرة؟',
        questionText_en: 'Four distinct symbols cycle across quadrants in a strict clockwise permutation per row and column step. Which configuration correctly fills the target cell?',
        stimulusHtml: render3x3Matrix(cells, 310),
        options,
        correctIndex,
        explanation_ar: `إزاحة الموضع الدوري عند الخلية [3, 3] تعيد الرموز إلى حالتها المرجعية الأولية بعد دورة كاملة (4 خطوات)، وهو ما يطابق الخيار (${String.fromCharCode(65 + correctIndex)}).`,
        explanation_en: `With total rotational displacement modulo 4 evaluating to 0 at index (2, 2), the elements align identically to the initial configuration in (${String.fromCharCode(65 + correctIndex)}).`
      };
    }
  },

  // 8. Geometric Segment Overlap Summation
  {
    id: 'abs_segment_addition',
    domain: 'abstract_reasoning',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 40,
    generate(prng, difficulty = 2) {
      // 4 possible segments: Top, Right, Bottom, Left
      const drawFrameSegments = (segs) => (s) => {
        const d = s * 0.36;
        let out = `<rect x="${-d}" y="${-d}" width="${d * 2}" height="${d * 2}" fill="none" stroke="currentColor" stroke-opacity="0.15" stroke-width="1"/>`;
        if (segs.top) out += `<line x1="${-d}" y1="${-d}" x2="${d}" y2="${-d}" stroke="#38bdf8" stroke-width="3.5" stroke-linecap="round"/>`;
        if (segs.right) out += `<line x1="${d}" y1="${-d}" x2="${d}" y2="${d}" stroke="#ec4899" stroke-width="3.5" stroke-linecap="round"/>`;
        if (segs.bottom) out += `<line x1="${d}" y1="${d}" x2="${-d}" y2="${d}" stroke="#10b981" stroke-width="3.5" stroke-linecap="round"/>`;
        if (segs.left) out += `<line x1="${-d}" y1="${d}" x2="${-d}" y2="${-d}" stroke="#f59e0b" stroke-width="3.5" stroke-linecap="round"/>`;
        return out;
      };

      // Rule: Cell 3 is the Union of Cell 1 and Cell 2 segments in each row
      const r1c1 = { top: true, right: false, bottom: false, left: false };
      const r1c2 = { top: false, right: true, bottom: false, left: false };
      const r1c3 = { top: true, right: true, bottom: false, left: false };

      const r2c1 = { top: false, right: false, bottom: true, left: false };
      const r2c2 = { top: false, right: false, bottom: false, left: true };
      const r2c3 = { top: false, right: false, bottom: true, left: true };

      const r3c1 = { top: true, right: false, bottom: true, left: false };
      const r3c2 = { top: false, right: true, bottom: false, left: true };
      const r3c3 = { top: true, right: true, bottom: true, left: true }; // All four sides

      const cells = [
        drawFrameSegments(r1c1), drawFrameSegments(r1c2), drawFrameSegments(r1c3),
        drawFrameSegments(r2c1), drawFrameSegments(r2c2), drawFrameSegments(r2c3),
        drawFrameSegments(r3c1), drawFrameSegments(r3c2), drawFrameSegments(r3c3)
      ];

      const options = [
        { id: 'A', svg: renderOptionSvg(drawFrameSegments(r3c3), 95, 'All four sides'), value: 'all' },
        { id: 'B', svg: renderOptionSvg(drawFrameSegments({ top: true, right: false, bottom: true, left: false }), 95, 'Top and bottom only'), value: 'tb' },
        { id: 'C', svg: renderOptionSvg(drawFrameSegments({ top: false, right: true, bottom: false, left: true }), 95, 'Right and left only'), value: 'rl' },
        { id: 'D', svg: renderOptionSvg(drawFrameSegments({ top: true, right: true, bottom: false, left: false }), 95, 'Top right only'), value: 'tr' }
      ];

      const shuffled = prng.shuffle(options);
      const correctIndex = shuffled.findIndex(o => o.value === 'all');

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: 'الخلية الثالثة في كل صف تمثل اتحاد وتجميع القطع المستقيمة المكونة للخليتين السابقتين معًا. ما هو الشكل الناتج في المربع الناقص؟',
        questionText_en: 'In each row, the third cell represents the logical union of line segments from the preceding two cells. Which figure correctly completes the final cell?',
        stimulusHtml: render3x3Matrix(cells, 310),
        options: shuffled,
        correctIndex,
        explanation_ar: `عند دمج الضلعين الرأسيين مع الضلعين الأفقيين في الصف الثالث نحصل على إطار مربع كامل الأضلاع الأربعة، وهو ما يتطابق تمامًا مع الخيار (${shuffled[correctIndex].id}).`,
        explanation_en: `Superimposing the horizontal segments (top and bottom) with the vertical segments (left and right) yields a complete 4-sided perimeter frame, identifying (${shuffled[correctIndex].id}).`
      };
    }
  },

  // 9. Intersection Point Triangular Number Growth
  {
    id: 'abs_spatial_intersection_count',
    domain: 'abstract_reasoning',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 40,
    generate(prng, difficulty = 2) {
      // Series of lines intersecting: 1 line (0 pts), 2 intersecting (1 pt), 3 pairwise intersecting (3 pts), 4 (6 pts), 5 (10 pts)
      const drawIntersections = (linesCount) => (s) => {
        let out = '';
        const r = s * 0.42;
        for (let i = 0; i < linesCount; i++) {
          const a = (i * Math.PI) / linesCount;
          const x1 = Math.cos(a) * r;
          const y1 = Math.sin(a) * r;
          out += `<line x1="${(-x1).toFixed(1)}" y1="${(-y1).toFixed(1)}" x2="${x1.toFixed(1)}" y2="${y1.toFixed(1)}" stroke="#38bdf8" stroke-width="2.2"/>`;
        }
        return out;
      };

      const series = [
        drawIntersections(1), // 0 points
        drawIntersections(2), // 1 point
        drawIntersections(3), // 3 points
        drawIntersections(4), // 6 points
        null
      ];

      const options = [
        { id: 'A', svg: renderOptionSvg(drawIntersections(5), 95, '5 intersecting lines'), value: 5 },
        { id: 'B', svg: renderOptionSvg(drawIntersections(4), 95, '4 intersecting lines'), value: 4 },
        { id: 'C', svg: renderOptionSvg(drawIntersections(6), 95, '6 intersecting lines'), value: 6 },
        { id: 'D', svg: renderOptionSvg((s) => DrawShapes.circle(s * 0.7, false, '#ec4899'), 95, 'Circle'), value: -1 }
      ];

      const shuffled = prng.shuffle(options);
      const correctIndex = shuffled.findIndex(o => o.value === 5);

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: 'تزيد عدد المستقيمات المتقاطعة بمقدار 1 في كل خطوة، مما يولد متتالية أعداد مثلثية لنقاط التقاطع (0، 1، 3، 6، ...). ما هو الشكل المكمل للسلسلة؟',
        questionText_en: 'The number of lines increments by 1 per frame, expanding intersection nodes according to triangular numbers (0, 1, 3, 6, 10). Which configuration belongs at the question mark?',
        stimulusHtml: renderHorizontalSeries(series, 360),
        options: shuffled,
        correctIndex,
        explanation_ar: `السلسلة تتبع عدد المستقيمات (1، 2، 3، 4) وبالتالي يتطلب الإطار الخامس وجود 5 مستقيمات متقاطعة (10 نقاط تقاطع)، وهو ما يمثله الخيار (${shuffled[correctIndex].id}).`,
        explanation_en: `Progressing from 1 through 4 lines dictates exactly 5 intersecting lines for the fifth frame, corresponding strictly to option (${shuffled[correctIndex].id}).`
      };
    }
  },

  // 10. Concentric Polygon Edge Progression
  {
    id: 'abs_concentric_polygons',
    domain: 'abstract_reasoning',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 40,
    generate(prng, difficulty = 2) {
      const drawPolyEdges = (sides, s, color) => {
        const r = s * 0.44;
        const pts = [];
        for (let i = 0; i < sides; i++) {
          const a = (i * 2 * Math.PI) / sides - Math.PI / 2;
          pts.push(`${(Math.cos(a) * r).toFixed(1)},${(Math.sin(a) * r).toFixed(1)}`);
        }
        return `<polygon points="${pts.join(' ')}" fill="none" stroke="${color}" stroke-width="2.2"/>`;
      };

      const drawConcentricPair = (outerSides, innerSides) => (s) => {
        let out = '';
        out += drawPolyEdges(outerSides, s, '#38bdf8');
        out += drawPolyEdges(innerSides, s * 0.58, '#ec4899');
        return out;
      };

      // Rows: Outer sides = 3 + r, Columns: Inner sides = 3 + c
      const cells = [];
      for (let r = 0; r < 3; r++) {
        for (let c = 0; c < 3; c++) {
          cells.push(drawConcentricPair(3 + r, 3 + c));
        }
      }

      // (2, 2): Outer = 5 (Pentagon), Inner = 5 (Pentagon)
      const correctOuter = 5;
      const correctInner = 5;

      const distractors = [
        { o: 5, i: 4 },
        { o: 4, i: 5 },
        { o: 6, i: 5 }
      ];

      const all = prng.shuffle([{ o: correctOuter, i: correctInner }, ...distractors]);
      const correctIndex = all.findIndex(x => x.o === 5 && x.i === 5);

      const options = all.map((it, idx) => ({
        id: String.fromCharCode(65 + idx),
        svg: renderOptionSvg(drawConcentricPair(it.o, it.i), 95, `${it.o} outer, ${it.i} inner`),
        value: `${it.o}-${it.i}`
      }));

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: 'يزيد عدد أضلاع المضلع الخارجي بمقدار ضلع واحد في كل صف (مثلث، مربع، مخمس)، بينما يزيد عدد أضلاع المضلع الداخلي عبر الأعمدة. ما هو الشكل الناقص؟',
        questionText_en: 'Outer polygon sides increment by 1 across rows (triangle, square, pentagon), while inner polygon sides increment across columns. Which figure completes the matrix?',
        stimulusHtml: render3x3Matrix(cells, 310),
        options,
        correctIndex,
        explanation_ar: `في الصف الأخير والعمود الأخير يكون كل من المضلع الخارجي والداخلي خماسي الأضلاع (مخمس داخل مخمس)، وهو ما يتطابق مع الخيار (${String.fromCharCode(65 + correctIndex)}).`,
        explanation_en: `Row 3 requires a 5-sided outer polygon and Column 3 requires a 5-sided inner polygon, specifying a pentagon within a pentagon in option (${String.fromCharCode(65 + correctIndex)}).`
      };
    }
  },

  // 11. Glide Reflection Matrix (Reflection + Orthogonal Shift)
  {
    id: 'abs_mirror_glide_symmetry',
    domain: 'abstract_reasoning',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 40,
    generate(prng, difficulty = 2) {
      // Shape is an asymmetric 'L' or arrow
      const drawFlag = (isFlippedH, isFlippedV) => (s) => {
        const sx = isFlippedH ? -1 : 1;
        const sy = isFlippedV ? -1 : 1;
        const d = s * 0.35;
        let out = `<g transform="scale(${sx}, ${sy})">`;
        out += `<line x1="0" y1="${d}" x2="0" y2="${-d}" stroke="#38bdf8" stroke-width="3" stroke-linecap="round"/>`;
        out += `<polygon points="0,${-d} ${d * 0.7},${-d * 0.5} 0,0" fill="#ec4899"/>`;
        out += `</g>`;
        return out;
      };

      // Row rule: alternates horizontal reflection
      // Column rule: alternates vertical reflection
      const cells = [];
      for (let r = 0; r < 3; r++) {
        for (let c = 0; c < 3; c++) {
          const flipH = c % 2 === 1;
          const flipV = r % 2 === 1;
          cells.push(drawFlag(flipH, flipV));
        }
      }

      // (2, 2) has c=2 (no flipH), r=2 (no flipV) -> identical to (0,0)
      const options = [
        { id: 'A', svg: renderOptionSvg(drawFlag(false, false), 95, 'Original'), value: '0-0' },
        { id: 'B', svg: renderOptionSvg(drawFlag(true, false), 95, 'Flip H only'), value: '1-0' },
        { id: 'C', svg: renderOptionSvg(drawFlag(false, true), 95, 'Flip V only'), value: '0-1' },
        { id: 'D', svg: renderOptionSvg(drawFlag(true, true), 95, 'Flip both'), value: '1-1' }
      ];

      const shuffled = prng.shuffle(options);
      const correctIndex = shuffled.findIndex(o => o.value === '0-0');

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: 'تخضع الراية لانعكاس أفقي عند كل انتقال لليمين، وانعكاس رأسي عند كل انتقال لأسفل. ما هو الاتجاه الصحيح للراية في الخلية الأخيرة؟',
        questionText_en: 'The flag reflects horizontally across each column and vertically down each row. Which orientation accurately occupies the bottom-right cell?',
        stimulusHtml: render3x3Matrix(cells, 310),
        options: shuffled,
        correctIndex,
        explanation_ar: `بانتقال خطوتين أفقيًا وخطوتين رأسيًا (انعكاس مزدوج مرتين)، يعود الشكل تمامًا إلى اتجاهه الأصلي الأول، المطابق للخيار (${shuffled[correctIndex].id}).`,
        explanation_en: `Two consecutive horizontal reflections cancel out, as do two vertical reflections, restoring the exact orientation of the initial frame in (${shuffled[correctIndex].id}).`
      };
    }
  },

  // 12. Clock-Face Radial Phase Shift
  {
    id: 'abs_radial_phase_shift',
    domain: 'abstract_reasoning',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 40,
    generate(prng, difficulty = 2) {
      const startHour = prng.int(1, 4);
      const stepHour = prng.pick([2, 3]);

      const drawClockHand = (hour) => (s) => {
        const angle = ((hour % 12) * 30 - 90) * (Math.PI / 180);
        const r = s * 0.42;
        const x = Math.cos(angle) * r;
        const y = Math.sin(angle) * r;
        let out = `<circle cx="0" cy="0" r="${r}" fill="none" stroke="currentColor" stroke-opacity="0.3" stroke-width="1.8"/>`;
        out += `<line x1="0" y1="0" x2="${x.toFixed(1)}" y2="${y.toFixed(1)}" stroke="#38bdf8" stroke-width="3.5" stroke-linecap="round"/>`;
        out += `<circle cx="0" cy="0" r="4.5" fill="#ffffff"/>`;
        out += `<circle cx="${x.toFixed(1)}" cy="${y.toFixed(1)}" r="4.5" fill="#ec4899"/>`;
        return out;
      };

      const series = [];
      for (let i = 0; i < 5; i++) {
        const h = startHour + i * stepHour;
        series.push(i === 4 ? null : drawClockHand(h));
      }

      const targetH = startHour + 4 * stepHour;
      const distractorHours = [targetH - 1, targetH + 1, targetH + 2];
      const all = prng.shuffle([targetH, ...distractorHours]);
      const correctIndex = all.indexOf(targetH);

      const options = all.map((h, idx) => ({
        id: String.fromCharCode(65 + idx),
        svg: renderOptionSvg(drawClockHand(h), 95, `Hour ${h % 12}`),
        value: h % 12
      }));

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: 'يتحرك عقرب الساعة بمقدار إزاحة منتظمة قدرها ساعات محددة مع كل إطار. ما هو الموضع الذي يشير إليه العقرب عند إشارة الاستفهام؟',
        questionText_en: 'The clock hand advances by a fixed hour interval per frame. Which position accurately completes the chronological sequence?',
        stimulusHtml: renderHorizontalSeries(series, 360),
        options,
        correctIndex,
        explanation_ar: `بإضافة ${stepHour} ساعات مع كل خطوة، يشير العقرب في الإطار الخامس إلى الساعة ${(targetH % 12) || 12}، وهو ما يطابق الخيار (${String.fromCharCode(65 + correctIndex)}).`,
        explanation_en: `Advancing ${stepHour} hours per interval places the indicator hand precisely at position ${(targetH % 12) || 12} on the fifth step, matching (${String.fromCharCode(65 + correctIndex)}).`
      };
    }
  },

  // 13. Shading Density / Perimeter Strip Hatching
  {
    id: 'abs_perimeter_density',
    domain: 'abstract_reasoning',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 40,
    generate(prng, difficulty = 2) {
      const drawStripes = (stripeCount) => (s) => {
        const w = s * 0.8;
        const h = s * 0.8;
        let out = `<rect x="${-w / 2}" y="${-h / 2}" width="${w}" height="${h}" rx="6" fill="rgba(15, 23, 42, 0.4)" stroke="currentColor" stroke-width="1.8"/>`;
        if (stripeCount > 0) {
          const step = w / (stripeCount + 1);
          for (let i = 1; i <= stripeCount; i++) {
            const x = -w / 2 + i * step;
            out += `<line x1="${x.toFixed(1)}" y1="${-h / 2}" x2="${x.toFixed(1)}" y2="${h / 2}" stroke="#38bdf8" stroke-width="2.5"/>`;
          }
        }
        return out;
      };

      // Strip count = r + c * 2 + 1
      const cells = [];
      for (let r = 0; r < 3; r++) {
        for (let c = 0; c < 3; c++) {
          const cnt = r + c + 1;
          cells.push(drawStripes(cnt));
        }
      }

      const targetCount = 2 + 2 + 1; // 5 stripes
      const distractors = [3, 4, 6];
      const allCounts = prng.shuffle([targetCount, ...distractors]);
      const correctIndex = allCounts.indexOf(targetCount);

      const options = allCounts.map((cnt, idx) => ({
        id: String.fromCharCode(65 + idx),
        svg: renderOptionSvg(drawStripes(cnt), 95, `${cnt} stripes`),
        value: cnt
      }));

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: 'تزداد كثافة الخطوط الرأسية داخل المربع بمقدار خط واحد مع كل حركة أفقية ورأسية في المصفوفة. كم خطًا يجب أن تحتوي الخلية الأخيرة؟',
        questionText_en: 'The internal hatching frequency increments by 1 vertical bar across both rows and columns. How many bars belong in the missing cell?',
        stimulusHtml: render3x3Matrix(cells, 310),
        options,
        correctIndex,
        explanation_ar: `تتابع كثافة الخطوط نمط الجمع (الصف + العمود + 1)، مما يعطي في الخلية الأخيرة 2 + 2 + 1 = 5 خطوط رأسية، المطابقة للخيار (${String.fromCharCode(65 + correctIndex)}).`,
        explanation_en: `Evaluating the linear index sum (row + col + 1) at position (2, 2) yields exactly 5 stripes, confirming option (${String.fromCharCode(65 + correctIndex)}).`
      };
    }
  },

  // 14. Proportional Dimensional Scaling Matrix
  {
    id: 'abs_matrix_scale_ratio',
    domain: 'abstract_reasoning',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 40,
    generate(prng, difficulty = 2) {
      const drawEllipse = (wScale, hScale) => (s) => {
        const rx = s * 0.16 * wScale;
        const ry = s * 0.16 * hScale;
        return `<ellipse cx="0" cy="0" rx="${rx.toFixed(1)}" ry="${ry.toFixed(1)}" fill="#38bdf8" fill-opacity="0.25" stroke="#38bdf8" stroke-width="2.2"/>`;
      };

      // Rows: height scale = 1, 1.5, 2. Columns: width scale = 1, 1.5, 2
      const cells = [];
      for (let r = 0; r < 3; r++) {
        for (let c = 0; c < 3; c++) {
          cells.push(drawEllipse(1 + c * 0.5, 1 + r * 0.5));
        }
      }

      // (2, 2) has w=2, h=2 (large circle)
      const targetW = 2;
      const targetH = 2;

      const distractors = [
        { w: 1, h: 2 },
        { w: 2, h: 1 },
        { w: 1.5, h: 1.5 }
      ];

      const all = prng.shuffle([{ w: targetW, h: targetH }, ...distractors]);
      const correctIndex = all.findIndex(item => item.w === 2 && item.i === undefined && item.h === 2);

      const options = all.map((it, idx) => ({
        id: String.fromCharCode(65 + idx),
        svg: renderOptionSvg(drawEllipse(it.w, it.h), 95, `Scale ${it.w}x${it.h}`),
        value: `${it.w}-${it.h}`
      }));

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: 'يتمدد الشكل البيضاوي أفقيًا بمعدل ثابت عبر الأعمدة، ويتمدد رأسيًا بمعدل ثابت عبر الصفوف. ما هو التناسب الهندسي للخلية الناقصة؟',
        questionText_en: 'The ellipse scales horizontally across columns and vertically down rows in uniform ratios. Which figure accurately completes the scale matrix?',
        stimulusHtml: render3x3Matrix(cells, 310),
        options,
        correctIndex,
        explanation_ar: `في الموضع الأخير يصل معامل التمدد الأفقي إلى أقصاه (2x) والرأسي إلى أقصاه (2x)، فيتحول الشكل إلى دائرة متكافئة الأقطار مطابقة للخيار (${String.fromCharCode(65 + correctIndex)}).`,
        explanation_en: `At index (2, 2), both horizontal and vertical scaling reach maximum magnitude (2x), producing a symmetric circle matching option (${String.fromCharCode(65 + correctIndex)}).`
      };
    }
  },

  // 15. Shape Morphological Deformation Series
  {
    id: 'abs_shape_morphology',
    domain: 'abstract_reasoning',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 40,
    generate(prng, difficulty = 2) {
      // Transition from Circle (rx = s*0.4, ry = s*0.4) to Diamond/Cross
      const drawMorph = (stage) => (s) => {
        const d = s * 0.42;
        const cornerRadius = Math.max(0, (4 - stage) * 10);
        return `<rect x="${-d}" y="${-d}" width="${d * 2}" height="${d * 2}" rx="${cornerRadius}" fill="rgba(56, 189, 248, 0.15)" stroke="#38bdf8" stroke-width="2.5"/>`;
      };

      const series = [
        drawMorph(0), // Rounded pill / circle-like
        drawMorph(1), // Smooth rounded rect
        drawMorph(2), // Less rounded rect
        drawMorph(3), // Sharp cornered rect
        null
      ];

      // Stage 4 has rx=0 (pure square) with an inscribed diamond
      const drawFinalStage = (s) => {
        const d = s * 0.42;
        return `<rect x="${-d}" y="${-d}" width="${d * 2}" height="${d * 2}" rx="0" fill="rgba(236, 72, 153, 0.2)" stroke="#ec4899" stroke-width="2.5"/><polygon points="0,${-d} ${d},0 0,${d} ${-d},0" fill="none" stroke="#38bdf8" stroke-width="1.8"/>`;
      };

      const options = [
        { id: 'A', svg: renderOptionSvg(drawFinalStage, 95, 'Sharp rectangle with inner diamond'), value: 'target' },
        { id: 'B', svg: renderOptionSvg(drawMorph(0), 95, 'Fully rounded'), value: 'stage0' },
        { id: 'C', svg: renderOptionSvg(drawMorph(1), 95, 'Semi rounded'), value: 'stage1' },
        { id: 'D', svg: renderOptionSvg((s) => DrawShapes.circle(s * 0.4, true, '#10b981'), 95, 'Solid circle'), value: 'circle' }
      ];

      const shuffled = prng.shuffle(options);
      const correctIndex = shuffled.findIndex(o => o.value === 'target');

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: 'تتحول الزوايا تدريجيًا من الاستدارة التامة نحو الحدة المستقيمة، يعقبها نشوء شكل داخلي مكمل. ما هو الشكل الذي يتم السلسلة؟',
        questionText_en: 'The shape undergoes continuous corner sharpening from fully rounded to acute square geometry, followed by internal facet emergence. Which option represents the terminal state?',
        stimulusHtml: renderHorizontalSeries(series, 360),
        options: shuffled,
        correctIndex,
        explanation_ar: `تتناقص نصف قطر الزاوية بانتظام حتى الصفر، وفي المرحلة الأخيرة ينشأ المضلع الماسي الداخلي المكمل، مما يحدد الخيار (${shuffled[correctIndex].id}).`,
        explanation_en: `Corner radii decrement monotonically to zero, followed by the emergence of the inscribed rhombic facet, identifying (${shuffled[correctIndex].id}).`
      };
    }
  }
];
